import { Button, Card, Col, Divider, Flex, Form, Input, Row } from 'antd'
import type { Rule } from 'antd/es/form'
import { Check, CircleX, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { PhoneInput, Text, TitleSection } from 'src/components'
import { useDocumentTitle } from 'src/hooks'

import { useBilling, useSections, type FormState } from '../hooks'
import { useStyles } from '../styles'

export const Billing = () => {
  const { sections } = useSections()
  const { isEditing, setIsEditing, onEdit, isPending, form, initialValues } =
    useBilling()

  const { t } = useTranslation()
  const { styles } = useStyles()

  useDocumentTitle(t('menu.billing'))

  const onCancel = () => setIsEditing(false)

  return (
    <Flex vertical gap={20}>
      <TitleSection title={t('menu.billing')} />

      <Card
        variant="outlined"
        actions={[
          <Flex
            key="billingEdit"
            align="center"
            justify="end"
            gap={12}
            className={styles.actions}
          >
            {isEditing && (
              <Button
                onClick={onCancel}
                icon={<CircleX size={16} />}
                disabled={isPending}
              >
                {t('cancel')}
              </Button>
            )}
            <Button
              type="primary"
              icon={isEditing ? <Check size={16} /> : <Pencil size={16} />}
              onClick={onEdit}
              loading={isPending}
              className={styles.buttonEdit}
            >
              {isEditing ? t('save') : t('edit')}
            </Button>
          </Flex>
        ]}
      >
        <Form form={form} layout="vertical" initialValues={initialValues}>
          {sections.map(({ title, description, fields }, i) => (
            <div key={i}>
              <Row gutter={[20, 0]}>
                <Col span={8}>
                  <Flex vertical>
                    <Text size={16} weight={600}>
                      {title}
                    </Text>
                    <Text color="colorTextSecondary">{description}</Text>
                  </Flex>
                </Col>

                <Col span={16}>
                  <Flex vertical gap={isEditing ? 0 : 12}>
                    {fields.map(({ name, label, rules }) => (
                      <Row key={name} gutter={16} align="middle">
                        <Col span={6}>
                          <Text color="colorTextSecondary">{label}</Text>
                        </Col>
                        <Col span={18}>
                          {isEditing ? (
                            name === 'phone' ? (
                              <Form.Item
                                name={name}
                                rules={rules as Rule[]}
                                style={{ marginBottom: 12 }}
                              >
                                <PhoneInput />
                              </Form.Item>
                            ) : (
                              <Form.Item
                                name={name}
                                rules={rules as Rule[]}
                                style={{ marginBottom: 12 }}
                              >
                                <Input />
                              </Form.Item>
                            )
                          ) : (
                            <Text>
                              {initialValues[name as keyof FormState] || '—'}
                            </Text>
                          )}
                        </Col>
                      </Row>
                    ))}
                  </Flex>
                </Col>
              </Row>
              {i !== sections.length - 1 && <Divider />}
            </div>
          ))}
        </Form>
      </Card>
    </Flex>
  )
}
